/**
 * Normalises API failures into one shape.
 *
 * The API answers every failure with { error: { code, message, requestId } },
 * where the message is safe to show a user. Anything unexpected is reported as
 * INTERNAL_ERROR with a generic message, so the server never leaks detail.
 */
export class ApiError extends Error {
  constructor({ status, code, message, requestId, details, cause }) {
    super(message, { cause });
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.details = details;
  }
}

// Wording the user can act on, for the codes where the server's own phrasing is
// aimed at a developer. Codes absent here keep the server's message.
const FRIENDLY = {
  UNAUTHORIZED: "Your session has expired. Please sign in again.",
  FORBIDDEN: "You do not have permission to do that.",
  RATE_LIMITED: "Too many requests. Please wait a moment and try again.",
  ADDRESS_LOOKUP_FAILED:
    "Address lookup is unavailable. Please enter the address manually.",
  AI_UNAVAILABLE: "Support chat is unavailable. Please try again shortly.",
  INTERNAL_ERROR: "Something went wrong. Please try again.",
  NETWORK: "Could not reach the server. Check your connection and try again.",
};

// Image requests ask for a blob, so an error body arrives as one too. Duck typed
// rather than `instanceof Blob`: jsdom's Blob is a different, text()-less class.
const readEnvelope = async (data) => {
  let body = data;
  if (typeof body?.text === "function") {
    try {
      body = JSON.parse(await body.text());
    } catch {
      return null;
    }
  }
  return body?.error?.code ? body.error : null;
};

export const toApiError = async (error) => {
  if (error instanceof ApiError) return error;

  const response = error?.response;
  if (!response) {
    return new ApiError({
      code: "NETWORK",
      message: FRIENDLY.NETWORK,
      cause: error,
    });
  }

  const envelope = await readEnvelope(response.data);
  const code = envelope?.code ?? "INTERNAL_ERROR";
  const status = response.status;
  const message =
    FRIENDLY[code] ?? envelope?.message ?? FRIENDLY.INTERNAL_ERROR;

  return new ApiError({
    status,
    code,
    // Only a server fault is worth reporting to support, and only then is the
    // id worth the noise. It rides on the message so the existing snackbar
    // call sites show it without changing.
    message:
      status >= 500 && envelope?.requestId
        ? `${message} (ref: ${envelope.requestId})`
        : message,
    requestId: envelope?.requestId,
    details: envelope?.details,
    cause: error,
  });
};
